import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from './apiUtils';
import { AlertCircle, FileText, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './components/ui/alert';
import { Button } from './components/ui/button';
import DateRangeSelector from './components/DateRangeSelector';
import { useDateRangeSelector } from './hooks/useDateRangeSelector';
import { getCurrentUser } from './authUtils';

function StockTransferReport() {
  // Date range selector hook
  const dateSelector = useDateRangeSelector();
  
  const { 
    reportType, 
    selectedDate, 
    selectedYear, 
    selectedMonth, 
    selectedWeek,
    customStartDate,
    customEndDate,
    getDateRange 
  } = dateSelector;
  
  // Stock transfer data state - grouped by shop then by date
  const [shiftedInByShop, setShiftedInByShop] = useState([]); // [{ shopName, shopCode, dates: [{ date, items: [...] }] }]
  const [shiftedOutByShop, setShiftedOutByShop] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get user data
  const user = getCurrentUser();
  const shopName = user?.shopName || 'Easy Sheets, Daily';

  // Format date as DD-MM-YYYY
  const formatDateDMY = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Fetch stock transfer data using single API call for date range
  const fetchStockTransferData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const { startDate, endDate } = getDateRange();
      
      // Single API call for the entire date range (instead of 60+ calls)
      const response = await apiGet(`/api/stock-transfers/range?startDate=${startDate}&endDate=${endDate}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load data (${response.status})`);
      }
      
      const data = await response.json();
      const { shiftedInByDate = {}, shiftedOutByDate = {} } = data;
      
      // Group SHIFTED IN by source shop, then by date
      const inByShopMap = new Map();
      for (const [date, items] of Object.entries(shiftedInByDate)) {
        for (const item of items) {
          const shopKey = `${item.supplierName}|${item.supplierCode}`;
          if (!inByShopMap.has(shopKey)) {
            inByShopMap.set(shopKey, {
              shopName: item.supplierName,
              shopCode: item.supplierCode,
              dateMap: new Map()
            });
          }
          const shop = inByShopMap.get(shopKey);
          if (!shop.dateMap.has(date)) {
            shop.dateMap.set(date, []);
          }
          shop.dateMap.get(date).push({ ...item, transferDate: date });
        }
      }
      
      // Convert to array format
      const inByShopArray = Array.from(inByShopMap.values()).map(shop => ({
        shopName: shop.shopName,
        shopCode: shop.shopCode,
        dates: Array.from(shop.dateMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([date, items]) => ({ date, items }))
      }));
      
      // Group SHIFTED OUT by destination shop, then by date
      const outByShopMap = new Map();
      for (const [date, items] of Object.entries(shiftedOutByDate)) {
        for (const item of items) {
          const shopKey = `${item.supplierName}|${item.supplierCode}`;
          if (!outByShopMap.has(shopKey)) {
            outByShopMap.set(shopKey, {
              shopName: item.supplierName,
              shopCode: item.supplierCode,
              dateMap: new Map()
            });
          }
          const shop = outByShopMap.get(shopKey);
          if (!shop.dateMap.has(date)) {
            shop.dateMap.set(date, []);
          }
          shop.dateMap.get(date).push({ ...item, transferDate: date });
        }
      }
      
      // Convert to array format
      const outByShopArray = Array.from(outByShopMap.values()).map(shop => ({
        shopName: shop.shopName,
        shopCode: shop.shopCode,
        dates: Array.from(shop.dateMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([date, items]) => ({ date, items }))
      }));
      
      setShiftedInByShop(inByShopArray);
      setShiftedOutByShop(outByShopArray);
      
    } catch (error) {
      console.error('Error fetching stock transfer data:', error);
      setError('Failed to load stock transfer data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  // Effect to fetch data when date range changes
  useEffect(() => {
    fetchStockTransferData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, customStartDate, customEndDate]);

  // PDF generation
  const generatePDF = () => {
    // Generate pages - each page is one supplier+date or destination+date combination
    const pages = [];

    // STOCK IN pages - one page per supplier per date
    shiftedInByShop.forEach(shop => {
      shop.dates.forEach(dateGroup => {
        const itemsHtml = dateGroup.items.map((item, idx) => `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${item.brandName}</td>
            <td class="center">${item.sizeMl || ''}ml</td>
            <td class="center">${item.quantity}</td>
          </tr>
        `).join('');
        
        const totalQty = dateGroup.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        
        pages.push(`
          <div class="page">
            <div class="outer-box">
              <div class="title-section">
                <div class="title-text">STOCK IN</div>
              </div>
              
              <div class="inner-box info-box">
                <table class="info-table">
                  <tr>
                    <td class="label-cell">Shop Name:</td>
                    <td class="value-cell">${shopName}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Supplier:</td>
                    <td class="value-cell">${shop.shopName} (${shop.shopCode})</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Date:</td>
                    <td class="value-cell">${formatDateDMY(dateGroup.date)}</td>
                  </tr>
                </table>
              </div>
              
              <div class="separator-line"></div>
              
              <div class="inner-box data-box">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th style="width: 60px;">S.No</th>
                      <th>Brand Name</th>
                      <th style="width: 100px;">Size</th>
                      <th style="width: 100px;">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsHtml}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="3" class="total-label"><strong>Total</strong></td>
                      <td class="center total-value"><strong>${totalQty}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        `);
      });
    });

    // STOCK OUT pages - one page per destination per date
    shiftedOutByShop.forEach(shop => {
      shop.dates.forEach(dateGroup => {
        const itemsHtml = dateGroup.items.map((item, idx) => `
          <tr>
            <td class="center">${idx + 1}</td>
            <td>${item.brandName}</td>
            <td class="center">${item.sizeMl || ''}ml</td>
            <td class="center">${Math.abs(item.quantity)}</td>
          </tr>
        `).join('');
        
        const totalQty = dateGroup.items.reduce((sum, item) => sum + Math.abs(item.quantity || 0), 0);
        
        pages.push(`
          <div class="page">
            <div class="outer-box">
              <div class="title-section">
                <div class="title-text">STOCK OUT</div>
              </div>
              
              <div class="inner-box info-box">
                <table class="info-table">
                  <tr>
                    <td class="label-cell">Shop Name:</td>
                    <td class="value-cell">${shopName}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Destination:</td>
                    <td class="value-cell">${shop.shopName} (${shop.shopCode})</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Date:</td>
                    <td class="value-cell">${formatDateDMY(dateGroup.date)}</td>
                  </tr>
                </table>
              </div>
              
              <div class="separator-line"></div>
              
              <div class="inner-box data-box">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th style="width: 60px;">S.No</th>
                      <th>Brand Name</th>
                      <th style="width: 100px;">Size</th>
                      <th style="width: 100px;">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsHtml}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="3" class="total-label"><strong>Total</strong></td>
                      <td class="center total-value"><strong>${totalQty}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        `);
      });
    });

    // If no data, show a message
    if (pages.length === 0) {
      pages.push(`
        <div class="page">
          <p class="no-data">No stock transfers for selected period</p>
        </div>
      `);
    }

    // Join pages with page breaks
    const pagesHtml = pages.join('<div class="page-break"></div>');

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Stock Transfer Report - ${shopName}</title>
  <style>
    @page { 
      margin: 10mm; 
      size: A4 portrait;
      @top-left { content: ''; }
      @top-center { content: ''; }
      @top-right { content: ''; }
      @bottom-left { content: ''; }
      @bottom-center { content: ''; }
      @bottom-right { content: ''; }
    }
    @media print {
      html, body { margin: 0; padding: 0; }
    }
    body { font-family: Arial, sans-serif; font-size: 12px; }
    .page { }
    .outer-box { border: 1px solid #000; padding: 3px; }
    .title-section { padding: 8px; border-bottom: 1px solid #000; margin: -3px -3px 3px -3px; }
    .title-text { text-align: center; font-size: 18px; font-weight: bold; }
    .inner-box { border: 1px solid #000; }
    .info-box { margin-bottom: 0; }
    .info-table { width: 100%; border-collapse: collapse; }
    .info-table td { padding: 6px; border: none; border-bottom: 1px solid #000; }
    .info-table tr:last-child td { border-bottom: none; }
    .label-cell { width: 180px; font-weight: bold; }
    .value-cell { }
    .separator-line { border-bottom: 1px solid #000; margin: 3px -3px; }
    .data-box { }
    .data-table { width: 100%; border-collapse: collapse; }
    .data-table th, .data-table td { border: 1px solid #000; padding: 6px; }
    .data-table th { background: #f0f0f0; text-align: center; }
    .data-table thead tr:first-child th { border-top: none; }
    .data-table thead th:first-child { border-left: none; }
    .data-table thead th:last-child { border-right: none; }
    .data-table tbody td:first-child { border-left: none; }
    .data-table tbody td:last-child { border-right: none; }
    .data-table tfoot td:first-child { border-left: none; }
    .data-table tfoot td:last-child { border-right: none; border-bottom: none; }
    .data-table tfoot td { border-bottom: none; }
    .data-box { border-right: 2px solid #000; }
    .center { text-align: center; }
    .total-label { text-align: right; padding-right: 10px; }
    .total-value { font-weight: bold; }
    .no-data { text-align: center; color: #666; font-style: italic; padding: 40px; }
    .page-break { page-break-before: always; }
  </style>
</head>
<body>
  ${pagesHtml}
</body>
</html>
`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 600);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading stock transfer data...</span>
        </div>
      </div>
    );
  }

  return (
      <main className="w-full px-4 py-8 space-y-6 max-w-7xl mx-auto">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-left">Stock Transfer Report</h1>
        <p className="text-muted-foreground text-left">Shop-wise and date-wise stock transfer details</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

        <DateRangeSelector
          reportType={dateSelector.reportType}
          onReportTypeChange={dateSelector.setReportType}
          selectedDateObj={dateSelector.selectedDateObj}
          onDateSelect={dateSelector.handleDateSelect}
          selectedYear={dateSelector.selectedYear}
          onYearChange={dateSelector.handleYearChange}
          selectedMonth={dateSelector.selectedMonth}
          onMonthChange={dateSelector.handleMonthChange}
          selectedWeek={dateSelector.selectedWeek}
          onWeekChange={dateSelector.handleWeekChange}
          weeksInMonth={dateSelector.weeksInMonth}
          customStartDateObj={dateSelector.customStartDateObj}
          customEndDateObj={dateSelector.customEndDateObj}
          onCustomRangeSelect={dateSelector.handleCustomRangeSelect}
        onGenerateReport={fetchStockTransferData}
          loading={loading}
          getMaxDate={dateSelector.getCurrentBusinessDateObject}
        hideGenerateButton={true}
      />

      <div className="flex justify-center">
        <Button onClick={generatePDF} size="lg" className="gap-2">
          <FileText className="h-5 w-5" />
          Generate PDF Report
        </Button>
          </div>
    </main>
  );
}

export default StockTransferReport;
